import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Truck, PenTool, MapPin, MessageCircle } from 'lucide-react';
import { Navbar, Footer } from '../components/Layout';
import { CartContext, API } from '../App';
import { toast } from 'sonner';

const HERO_IMAGE = "https://res.cloudinary.com/dmxxscqpl/image/upload/v1777370240/hero_image_yabha6.png";
const CATEGORY_HOME = "https://res.cloudinary.com/dmxxscqpl/image/upload/v1777370231/category_home_or2bh8.png";
const CATEGORY_OFFICE = "https://res.cloudinary.com/dmxxscqpl/image/upload/v1777370232/category_office_sdrkg3.png";
const CATEGORY_CUSTOM = "https://res.cloudinary.com/dmxxscqpl/image/upload/v1777370230/category_custom_wcebev.png";
const WORKSHOP_IMAGE = "https://res.cloudinary.com/dmxxscqpl/image/upload/v1777370232/workshop_image_qrkqce.png";

const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useContext(CartContext);

  useEffect(() => {
    fetchFeaturedProducts();
  }, []);

  const fetchFeaturedProducts = async () => {
    try {
      const response = await fetch(`${API}/products`);
      const data = await response.json();
      setFeaturedProducts(data.slice(0, 6));
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product) => {
    addToCart(product);
    toast.success(`${product.name} added to cart`);
  };

  const handleWhatsApp = async () => {
    try {
      const response = await fetch(`${API}/whatsapp-link`);
      const data = await response.json();
      window.open(data.link, '_blank');
    } catch (error) {
      window.open('https://wa.me/919821767267?text=Hi, I am interested in your furniture collection.', '_blank');
    }
  };

  const categories = [
    { name: 'Home Furniture', slug: 'Home', image: CATEGORY_HOME, description: 'Sofas, Beds, Wardrobes' },
    { name: 'Office Furniture', slug: 'Office', image: CATEGORY_OFFICE, description: 'Desks, Chairs, Cabinets' },
    { name: 'Custom & Modular', slug: 'Custom', image: CATEGORY_CUSTOM, description: 'Kitchens, Wardrobes, Setups' },
  ];

  const features = [
    { icon: PenTool, title: 'Quality Craftsmanship', description: 'Every piece is crafted with precision and care by skilled artisans.' },
    { icon: Truck, title: 'Mumbai Delivery', description: 'Free delivery across Mumbai for orders above ₹50,000.' },
    { icon: MapPin, title: 'Custom Orders', description: 'Bespoke furniture tailored to your exact specifications.' },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative h-screen min-h-[600px]" data-testid="hero-section">
        <div className="absolute inset-0">
          <img 
            src={HERO_IMAGE} 
            alt="Premium Furniture" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-gradient"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div className="max-w-xl animate-fadeInUp">
            <p className="font-heading text-xs uppercase tracking-[0.3em] text-white/80 mb-4">
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight mb-6">
              Crafting Spaces,<br />
              <span className="text-[#C4A882]">Creating Comfort</span>
            </h1>
            <p className="font-body text-base sm:text-lg text-white/80 mb-8 leading-relaxed">
              Transform your home with handcrafted furniture that blends timeless elegance with modern functionality.
            </p>
            <Link 
              to="/products" 
              className="btn-primary inline-flex items-center group"
              data-testid="explore-collection-btn"
            >
              <span>Explore Collection</span>
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-24 bg-[#F9F9F8]" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-heading text-xs uppercase tracking-[0.2em] text-[#C4A882] mb-3">Collections</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#1A1A1A] tracking-tight">
              Browse by Category
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {categories.map((category, index) => (
              <Link 
                key={category.slug} 
                to={`/products?category=${category.slug}`}
                className="group relative h-[400px] overflow-hidden"
                data-testid={`category-card-${category.slug.toLowerCase()}`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <img 
                  src={category.image} 
                  alt={category.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 category-overlay"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <p className="font-body text-xs uppercase tracking-[0.15em] text-white/70 mb-1">{category.description}</p>
                  <h3 className="font-heading text-xl font-semibold text-white">{category.name}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-24" data-testid="featured-products-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-12">
            <div>
              <p className="font-heading text-xs uppercase tracking-[0.2em] text-[#C4A882] mb-3">Curated Selection</p>
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#1A1A1A] tracking-tight">
                Featured Products
              </h2>
            </div>
            <Link 
              to="/products" 
              className="mt-4 sm:mt-0 font-body text-sm text-[#1A1A1A] hover:text-[#C4A882] flex items-center group"
            >
              View All Products
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-gray-200 aspect-[4/3] mb-4"></div>
                  <div className="h-4 bg-gray-200 w-1/3 mb-2"></div>
                  <div className="h-5 bg-gray-200 w-2/3 mb-2"></div>
                  <div className="h-4 bg-gray-200 w-1/4"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredProducts.map((product, index) => (
                <div 
                  key={product.id} 
                  className="product-card group"
                  data-testid={`featured-product-${index}`}
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <Link to={`/products/${product.id}`}>
                    <div className="aspect-[4/3] overflow-hidden mb-4 bg-[#F9F9F8]">
                      <img 
                        src={(product.image_urls && product.image_urls.length > 0) ? product.image_urls[0] : (product.image_url || 'https://via.placeholder.com/400x300?text=in-dekor')} 
                        alt={product.name}
                        className="w-full h-full object-cover transition-transform duration-500"
                      />
                    </div>
                  </Link>
                  <p className="font-body text-xs uppercase tracking-[0.15em] text-[#666666] mb-1">
                    {product.category}
                  </p>
                  <Link to={`/products/${product.id}`}>
                    <h3 className="font-heading text-lg font-medium text-[#1A1A1A] mb-1 group-hover:text-[#C4A882] transition-colors">
                      {product.name}
                    </h3>
                  </Link>
                  <p className="font-heading text-lg font-semibold text-[#1A1A1A] mb-3">
                    {product.price_on_request ? 'Price on Request' : `₹${product.price.toLocaleString('en-IN')}`}
                  </p>
                  {product.price_on_request ? (
                    <button
                      onClick={() => window.open(`https://wa.me/919821767267?text=Hi, I am interested in ${encodeURIComponent(product.name)}`, '_blank')}
                      className="btn-secondary w-full text-sm flex items-center justify-center gap-2"
                      data-testid={`enquire-${product.id}`}
                    >
                      <MessageCircle className="w-4 h-4" />
                      Enquire on WhatsApp
                    </button>
                  ) : (
                    <button 
                      onClick={() => handleAddToCart(product)}
                      className="btn-secondary w-full text-sm"
                      data-testid={`add-to-cart-${product.id}`}
                    >
                      Add to Cart
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-[#1A1A1A]" data-testid="why-choose-us-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="font-heading text-xs uppercase tracking-[0.2em] text-[#C4A882] mb-3">Why Choose Us</p>
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white tracking-tight mb-6">
                Furniture Crafted with Passion
              </h2>
              <p className="font-body text-gray-400 leading-relaxed mb-8">
                At in-dekor™, we believe furniture is more than just functional—it's an expression of your style. 
                Our skilled craftsmen in Mumbai bring decades of experience to every piece we create.
              </p>
              
              <div className="space-y-6">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#C4A882]/10 mr-4 flex-shrink-0">
                      <feature.icon className="w-5 h-5 text-[#C4A882]" />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-medium text-white mb-1">{feature.title}</h3>
                      <p className="font-body text-sm text-gray-400">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <img 
                src={WORKSHOP_IMAGE}
                alt="Craftsman at work"
                className="w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* WhatsApp CTA */}
      <button 
        onClick={handleWhatsApp}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
        data-testid="whatsapp-btn"
      >
        <MessageCircle className="w-7 h-7 text-white" />
      </button>

      <Footer />
    </div>
  );
};

export default HomePage;
